import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="map"
export default class extends Controller {
  static values = {
    center: Object,
    assets: Array,
    defaultSelectPath: String
  }
  static targets = [ "render", "pin", "searchResult", "selectedAsset", "selectPathProvider" ]

  initialize() {
    // We need to load and initialize the map before our target
    // connection callbacks can run. They run before connect, so we
    // make a promise in initialize() that will be resolved at the
    // end of connect(). Our callbacks await this promise.
    this.connectedPromiseResolver;
    this.connectedPromise = new Promise((resolve, reject) => {
      this.connectedPromiseResolver = resolve;
    });
  }

  async connect() {
    this.pins = {};
    this.searchResults = {};

    const { Map } = await google.maps.importLibrary("maps")

    this.map = new Map(
      this.renderTarget,
      {
        zoom: 14,
        center: this.centerValue,
        mapId: 'DEMO_MAP_ID',
      }
    );

    this.streetView = this.map.getStreetView();

    // Configure the Street View UI
    this.streetView.setOptions({
      addressControlOptions: {
        position: google.maps.ControlPosition.TOP_RIGHT, // Moves the address bar + back button to the top right
      }
    });

    this.selectPath = this.defaultSelectPathValue

    // drop a pin if the clicked location is a POI
    // otherwise, do nothing (this allows us to support double-tap to zoom)
    google.maps.event.addListener(this.map, 'click', (e) => {
      if (e.placeId) {
        e.stop() // prevent the default info window from displaying
        let params = {lat: e.latLng.lat(), lng: e.latLng.lng(), place_id: e.placeId}

        let url = new URL(this.pathForPinDrop, window.location)
        const existingParams = Object.fromEntries(new URLSearchParams(url.search));
        url.search = new URLSearchParams({...params, ...existingParams}).toString();

        // optimistically drop a pin
        this.renderSearchResult(params)

        Turbo.visit(url, { frame: "map_overlay" })
      }
    });
    // With a longpress (tap and hold) event, we can drop pins elsewhere on the map.
    // google maps api doesn't have a built-in longpress event listener, so this is a workaround
    var mousedUp = false;
    google.maps.event.addListener(this.map, 'mousedown', (e) => {
      mousedUp = false;
      setTimeout(() => {
        if(mousedUp === false){
          let params = {lat: e.latLng.lat(), lng: e.latLng.lng()}
          if (e.placeId) params["place_id"] = e.placeId

          let url = new URL(this.pathForPinDrop, window.location)
          const existingParams = Object.fromEntries(new URLSearchParams(url.search));
          url.search = new URLSearchParams({...params, ...existingParams}).toString();

          // optimistically drop a pin
          this.renderSearchResult(params)

          Turbo.visit(url, { frame: "map_overlay" })
        }
      }, 300);
    });
    google.maps.event.addListener(this.map, 'mouseup', function(e){
      mousedUp = true;
    });
    google.maps.event.addListener(this.map, 'dragstart', function(e){
      mousedUp = true;
    });

    this.connectedPromiseResolver();
  }

  async selectPathProviderTargetConnected(element) {
    this.selectPath = element.dataset.selectPath
  }

  async selectPathProviderTargetDisconnected() {
    this.selectPath = this.defaultSelectPathValue
  }

  get pathForPinDrop() {
    return this.selectPath
  }

  renderSearchResult(element) {
    let searchResult = document.createElement("span")
    searchResult.dataset.mapTarget = "searchResult"
    searchResult.dataset.lat = element.lat
    searchResult.dataset.lng = element.lng
    searchResult.dataset.focus = "disabled"
    this.mapOverlay.appendChild(searchResult)
  }

  async searchResultTargetConnected(element) {
    // make sure the map is defined before adding pins
    await this.connectedPromise;
    let searchResult = {lat: element.dataset.lat, lng: element.dataset.lng, displayName: element.dataset.displayName, id: element.id, focus: element.dataset.focus != "disabled"}
    this.addSearchResult(searchResult)
  }

  async searchResultTargetDisconnected(element) {
    // make sure the map is defined before adding pins
    await this.connectedPromise;
    this.removeSearchResult(element.id)
  }

  async addSearchResult(searchResult) {
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");

    const svg = `<svg width="50" height="60" viewBox="0 0 50 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                   <path d="M25 2C15 2 8 10 8 18C8 26 25 54 25 54C25 54 42 26 42 18C42 10 35 2 25 2Z" fill="#FD9843"/>
                   <circle cx="25" cy="18" r="6" fill="white"/>
                 </svg>`;

    const icon = document.createElement('div');
    icon.innerHTML = svg;

    const marker = new AdvancedMarkerElement({
      map: this.map,
      position: { lat: parseFloat(searchResult.lat), lng: parseFloat(searchResult.lng) },
      content: icon,
    });

    this.searchResults[searchResult.id] = marker;

    if (searchResult.focus) {
      this.focusSearchResults();
    }
  }

  async removeSearchResult(searchResultKey) {
    this.searchResults[searchResultKey].setMap(null);
    delete this.searchResults[searchResultKey];
  }

  async focusSearchResults() {
    const { LatLngBounds } = await google.maps.importLibrary("core");
    const bounds = new LatLngBounds();

    Object.values(this.searchResults).forEach((searchResult) => {
      bounds.extend({ lat: searchResult.position.lat, lng: searchResult.position.lng });
    });
    this.map.panToBounds(bounds, 5000);
  }

  async pinTargetConnected(element) {
    // make sure the map is defined before adding pins
    await this.connectedPromise;
    let asset = {lat: element.dataset.lat, lng: element.dataset.lng, emoji: element.dataset.emoji, key: element.id, path: element.dataset.path, name: element.dataset.name}
    this.addAsset(asset)
  }

  async pinTargetDisconnected(element) {
    // make sure the map is defined before removing pins
    await this.connectedPromise;
    this.removeAsset(element.id)
  }

  async selectedAssetTargetConnected(element) {
    // make sure the map is defined before adding pins
    await this.connectedPromise;
    let asset = {lat: element.dataset.lat, lng: element.dataset.lng, emoji: element.dataset.emoji, key: element.id, path: element.dataset.path, isActive: true, name: element.dataset.name}
    this.addAsset(asset)
  }

  async selectedAssetTargetDisconnected(element) {
    // make sure the map is defined before removing pins
    await this.connectedPromise;
    this.removeAsset(element.id)
  }

  async removeAsset(assetKey) {
    this.pins[assetKey].setMap(null);
  }

  async addAsset(asset) {
    const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker");
    const icon = document.createElement('div');
    icon.innerHTML = `
                      <div class = "inner-white-circle">
                        <div class = "glyph" style="font-size: 18px;min-height:8px;min-width:5px;" aria-label="${asset.name}">${asset.emoji !== undefined ? asset.emoji : ""}</div>
                      </div>
                      `
    const svg = `
                <svg width="67" height="78" viewBox="0 0 67 78" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g filter="url(#filter0_d_34_2501)">
                    <path d="M18.7927 34.6074L18.8802 34.732C18.9872 34.8903 19.099 35.0437 19.2108 35.2115L31.8788 53.1637C32.0611 53.4218 32.3039 53.6327 32.5866 53.7783C32.8693 53.9239 33.1835 54 33.5024 54C33.8214 54 34.1356 53.9239 34.4183 53.7783C34.701 53.6327 34.9438 53.4218 35.126 53.1637L47.7649 35.2259C47.8865 35.0581 48.0031 34.8951 48.1198 34.7464L48.1976 34.6313C49.9054 32.0248 50.8692 29.0124 50.9876 25.911C51.106 22.8097 50.3745 19.7343 48.8701 17.0085C47.3658 14.2827 45.1443 12.0074 42.4394 10.4221C39.7345 8.83671 36.6464 8 33.5 8C30.3536 8 27.2655 8.83671 24.5606 10.4221C21.8557 12.0074 19.6342 14.2827 18.1299 17.0085C16.6255 19.7343 15.894 22.8097 16.0124 25.911C16.1308 29.0124 17.0946 32.0248 18.8024 34.6313L18.7927 34.6074ZM33.5024 18.0793C34.9446 18.0793 36.3544 18.5011 37.5535 19.2914C38.7526 20.0817 39.6872 21.205 40.239 22.5193C40.7909 23.8335 40.9353 25.2797 40.654 26.6748C40.3726 28.07 39.6782 29.3516 38.6584 30.3574C37.6387 31.3633 36.3394 32.0483 34.925 32.3258C33.5105 32.6034 32.0444 32.4609 30.712 31.9166C29.3797 31.3722 28.2408 30.4503 27.4396 29.2675C26.6384 28.0848 26.2108 26.6942 26.2108 25.2717C26.2108 23.3641 26.979 21.5347 28.3464 20.1859C29.7139 18.8371 31.5686 18.0793 33.5024 18.0793Z" fill="#5A9BD5"/>
                  </g>
                  <defs>
                    <filter id="filter0_d_34_2501" x="0" y="0" width="67" height="78" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                      <feOffset dy="8"/>
                      <feGaussianBlur stdDeviation="8"/>
                      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"/>
                      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_34_2501"/>
                      <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_34_2501" result="shape"/>
                    </filter>
                  </defs>
                </svg>
                `
    const glyphSvgPinElement = new PinElement({
        scale: 1.5,
        borderColor: asset.isActive ? "#FD9843" : "#123362",
        background: svg,
        glyph: icon
      });

    let marker = new AdvancedMarkerElement({
      map: this.map,
      position: { lat: parseFloat(asset.lat), lng: parseFloat(asset.lng) },
      content: glyphSvgPinElement.element,
    });

    // Add listener for when user clicks an asset
    google.maps.event.addListener(marker, 'click', () => {
      this.mapOverlay.src = asset.path;
    });

    this.pins[asset.key] = marker;
  }

  center(lat, lng) {
    this.map.panTo({
      lat: parseFloat(lat),
      lng: parseFloat(lng)
    })
  }

  get mapCenter() {
    return this.map.getCenter()
  }

  get mapOverlay() {
    return document.querySelector("turbo-frame#map_overlay")
  }

  disconnect() {
    // The javascript API is pretty unclear about how it's disposed of
  }
}
